import { useSortable } from '@dnd-kit/sortable';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import { COLORS } from '@/styles/tokens/colors';
import { WithClasses } from '@/styles/types';

import { DraggableListItemInnerComponentProps } from './DraggableListItem.types';
import { Draghandle } from './Draghandle';
import { getSortableItemStyle } from './getSortableItemStyle';

export interface DraggableListItemProps<T> {
  item: T;
  /** @deprecated Unused; @dnd-kit uses item.id instead. */
  index?: number;
  ItemInnerComponent: React.ComponentType<
    DraggableListItemInnerComponentProps<T>
  >;
  isLast: boolean;
  hasDragHandle: boolean;
  withDividers: boolean;
  disabled?: boolean;
  classes?: WithClasses<typeof useStyles>;
}

const useStyles = makeStyles<{
  isDragging: boolean;
  isLast: boolean;
  withDividers: boolean;
  isGrouped: boolean;
}>()((theme, { isDragging, isLast, withDividers, isGrouped }) => {
  const maybeDashedBorder = (() => {
    if (isGrouped) {
      return {
        backgroundImage: `linear-gradient(to right, ${COLORS.GRAY[300]} 50%, rgba(255,255,255,0) 0%)`,
        backgroundPosition: 'bottom',
        backgroundSize: `12px 1px`,
        backgroundRepeat: 'repeat-x',
      };
    }

    return {};
  })();

  return {
    root: {
      backgroundColor: isDragging ? COLORS.GRAY[50] : 'unset',
      opacity: isDragging ? 1 : 'unset',
      borderRadius: isDragging ? theme.spacing(1) : 'unset',
      transition: 'border-color 0.1s ease-in',
      ...maybeDashedBorder,
      borderBottom:
        !withDividers || isDragging || isLast || isGrouped
          ? `1px solid transparent`
          : `1px solid ${COLORS.GRAY[200]}`,
    },
  };
});

export function DraggableListItem<
  T extends { id: string; isGrouped?: boolean },
>({
  item,
  index: _index,
  ItemInnerComponent,
  isLast,
  hasDragHandle,
  withDividers,
  disabled = false,
  classes: classesProp,
}: DraggableListItemProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled,
  });

  const isGrouped = item.isGrouped ?? false;

  const { classes } = useStyles(
    {
      isDragging,
      isLast,
      withDividers,
      isGrouped,
    },
    {
      props: { classes: classesProp },
    }
  );

  const style = getSortableItemStyle({ transform, transition, isDragging });

  const MemoizedDraghandle = useMemo(() => {
    if (disabled) {
      return <Box />;
    }

    // In @dnd-kit, drag handle receives listeners and attributes
    return (
      <Draghandle
        sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        {...listeners}
        {...attributes}
      />
    );
  }, [listeners, attributes, disabled, isDragging]);

  // If no drag handle, the entire item is draggable
  const itemDragProps = useMemo(
    () => (hasDragHandle ? {} : { ...listeners, ...attributes }),
    [hasDragHandle, listeners, attributes]
  );

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={classes.root}
      {...itemDragProps}
    >
      <ItemInnerComponent
        key={item.id}
        item={item}
        Draghandle={MemoizedDraghandle}
        isDragging={isDragging}
      />
    </li>
  );
}
